import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/shared/utils/commonUtils"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipPrimitive.Root data-slot="tooltip" {...props} />
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/** System-wide tooltip content style (matches sidebar design): px-3 py-1.5 text-xs font-semibold rounded-md + animation */
const TOOLTIP_CONTENT_BASE =
  "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs font-semibold text-balance";

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: "default" | "light" | "dark" | "sidebar"
}) {
  const variantStyles = {
    default: "bg-primary text-primary-foreground border border-border",
    light: "bg-gray-50 text-gray-900 border border-gray-300 shadow-xl ring-1 ring-gray-200",
    dark: "bg-gray-900 text-white border border-gray-700 shadow-lg",
    sidebar: "bg-sidebar text-white border border-sidebar-primary/20 shadow-xl"
  };

  const arrowStyles = {
    default: "bg-primary fill-primary border-border",
    light: "bg-gray-50 fill-gray-50 border-gray-300",
    dark: "bg-gray-900 fill-gray-900 border-gray-700",
    sidebar: "bg-sidebar fill-sidebar border-sidebar-primary/20"
  };

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(TOOLTIP_CONTENT_BASE, variantStyles[variant], className)}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            "z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]",
            arrowStyles[variant]
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
